import {
  ECLIPSERA_BLOB_CACHE_STORAGE_NAME,
  ECLIPSERA_SHELL_CACHE_STORAGE_NAME,
} from '../constants/blobCacheName'

/** One small localStorage flag per manifest — avoids multi‑MB URL JSON hitting quota and failing silently. */
const BLOB_WARM_COMPLETE_VERSION = 'v6'

function blobWarmCompleteStorageKey(manifestFingerprint: string): string {
  return `eclipsera.blobWarmComplete.${BLOB_WARM_COMPLETE_VERSION}.${manifestFingerprint}`
}

function blobWarmAttemptedStorageKey(manifestFingerprint: string): string {
  return `eclipsera.blobWarmAttempted.${BLOB_WARM_COMPLETE_VERSION}.${manifestFingerprint}`
}

/** Persisted warm flags use value `'1'` at a key that already includes the manifest id. */
function isBlobWarmFlagSet(storageKey: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (localStorage.getItem(storageKey) === '1') return true
  } catch {
    /* access denied / disabled */
  }
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === '1') return true
  } catch {
    /* access denied / disabled */
  }
  return false
}

/**
 * URLs the CDN answered 404/410/403/401 for during a warm, per manifest key. They are never in
 * any cache, so an exact audit would otherwise report them missing on every boot and re-gate a
 * page whose assets are all present. Small (typically 0–20 entries) and cleared with the
 * manifest fingerprint, so a fixed asset is retried as soon as the manifest changes.
 */
const BLOB_GONE_VERSION = 'v1'
const BLOB_GONE_MAX_ENTRIES = 300

function blobGoneStorageKey(manifestFingerprint: string): string {
  return `eclipsera.blobGone.${BLOB_GONE_VERSION}.${manifestFingerprint}`
}

function readPersistedGoneUrls(manifestFingerprint: string): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(blobGoneStorageKey(manifestFingerprint))
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((u): u is string => typeof u === 'string'))
  } catch {
    return new Set()
  }
}

function persistGoneUrls(manifestFingerprint: string, gone: ReadonlySet<string>): void {
  if (typeof window === 'undefined' || gone.size === 0) return
  try {
    const merged = readPersistedGoneUrls(manifestFingerprint)
    gone.forEach((u) => merged.add(u))
    localStorage.setItem(
      blobGoneStorageKey(manifestFingerprint),
      JSON.stringify([...merged].slice(0, BLOB_GONE_MAX_ENTRIES)),
    )
  } catch {
    /* quota / private mode / disabled — the audit will simply re-check them next boot */
  }
}

/**
 * Whether requests from this page go through `public/sw.js`.
 *
 * Cache Storage is only reachable through the worker's fetch handler. A page loaded with a hard
 * reload (Ctrl+Shift+R / "Empty cache and hard reload"), with DevTools "Bypass for network", or in
 * a browser without service workers has no controller — its `<img>` and `fetch` calls go straight
 * to the network, and an entry sitting in Cache Storage does nothing for it.
 */
export function isPageControlledBySw(): boolean {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
  return navigator.serviceWorker.controller !== null
}

export type WarmableAsset = {
  url: string
  as?: 'image' | 'video' | 'audio'
}

type WarmupPlan = {
  immediateAssets: readonly WarmableAsset[]
  deferredAssets?: readonly WarmableAsset[]
  deferredHintAssets?: readonly WarmableAsset[]
  initialDelayMs?: number
  deferredDelayMs?: number
  immediateBatchSize?: number
  deferredBatchSize?: number
}

function normalizeAssets(assets: readonly WarmableAsset[]): WarmableAsset[] {
  const seen = new Set<string>()
  const normalized: WarmableAsset[] = []
  for (const asset of assets) {
    if (!asset?.url || seen.has(asset.url)) continue
    seen.add(asset.url)
    normalized.push({ url: asset.url, as: asset.as ?? 'image' })
  }
  return normalized
}

function scheduleIdle(callback: () => void, timeout: number): number {
  if (typeof window === 'undefined') return 0
  const win = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  }
  if (typeof win.requestIdleCallback === 'function') {
    return win.requestIdleCallback(callback, { timeout })
  }
  return window.setTimeout(callback, timeout)
}

export function createAssetWarmupController() {
  const warmedUrls = new Set<string>()
  const pendingWarmups = new Map<string, Promise<void>>()
  const hintedUrls = new Set<string>()
  let backgroundWarmupStarted = false

  function addPrefetchHint(asset: WarmableAsset) {
    if (typeof document === 'undefined' || hintedUrls.has(asset.url)) return
    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.as = asset.as ?? 'image'
    link.href = asset.url
    if (/^https?:\/\//i.test(asset.url)) link.crossOrigin = 'anonymous'
    document.head.appendChild(link)
    hintedUrls.add(asset.url)
  }

  function warmImage(url: string): Promise<void> {
    if (typeof window === 'undefined' || warmedUrls.has(url)) return Promise.resolve()
    const existing = pendingWarmups.get(url)
    if (existing) return existing

    const promise = new Promise<void>((resolve) => {
      const img = new Image()
      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        warmedUrls.add(url)
        pendingWarmups.delete(url)
        resolve()
      }

      img.decoding = 'async'
        ; (img as HTMLImageElement & { fetchPriority?: 'high' | 'low' | 'auto' }).fetchPriority = 'low'
      img.onload = done
      img.onerror = done
      img.src = url

      if (img.complete) done()
    })

    pendingWarmups.set(url, promise)
    return promise
  }

  function warmVideo(url: string): Promise<void> {
    if (typeof window === 'undefined' || warmedUrls.has(url)) return Promise.resolve()
    const existing = pendingWarmups.get(url)
    if (existing) return existing

    const promise = new Promise<void>((resolve) => {
      const video = document.createElement('video')
      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        warmedUrls.add(url)
        pendingWarmups.delete(url)
        video.removeAttribute('src')
        video.load()
        resolve()
      }

      video.preload = 'auto'
      video.muted = true
      video.playsInline = true
      video.oncanplaythrough = done
      video.onloadeddata = done
      video.onerror = done
      video.src = url
      video.load()
    })

    pendingWarmups.set(url, promise)
    return promise
  }

  function warmAudio(url: string): Promise<void> {
    if (typeof window === 'undefined' || warmedUrls.has(url)) return Promise.resolve()
    const existing = pendingWarmups.get(url)
    if (existing) return existing

    const promise = new Promise<void>((resolve) => {
      const audio = document.createElement('audio')
      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        warmedUrls.add(url)
        pendingWarmups.delete(url)
        audio.removeAttribute('src')
        audio.load()
        resolve()
      }

      audio.preload = 'auto'
      audio.oncanplaythrough = done
      audio.onloadeddata = done
      audio.onerror = done
      audio.src = url
      audio.load()
    })

    pendingWarmups.set(url, promise)
    return promise
  }

  async function warmAssetsInBatches(assets: readonly WarmableAsset[], batchSize: number) {
    const normalized = normalizeAssets(assets)
    normalized.forEach(addPrefetchHint)

    for (let i = 0; i < normalized.length; i += batchSize) {
      const batch = normalized.slice(i, i + batchSize)
      await Promise.all(
        batch.map((asset) => {
          if (asset.as === 'video') return warmVideo(asset.url)
          if (asset.as === 'audio') return warmAudio(asset.url)
          return warmImage(asset.url)
        }),
      )
    }
  }

  return {
    warmAssets(assets: readonly WarmableAsset[], batchSize = 6) {
      return warmAssetsInBatches(assets, batchSize)
    },
    startBackgroundWarmup({
      immediateAssets,
      deferredAssets = [],
      deferredHintAssets = [],
      initialDelayMs = 1500,
      deferredDelayMs = 2500,
      immediateBatchSize = 6,
      deferredBatchSize = 4,
    }: WarmupPlan) {
      if (typeof window === 'undefined' || backgroundWarmupStarted) return
      backgroundWarmupStarted = true

      const normalizedImmediate = normalizeAssets(immediateAssets)
      normalizedImmediate.forEach(addPrefetchHint)

      scheduleIdle(() => {
        void warmAssetsInBatches(normalizedImmediate, immediateBatchSize).then(() => {
          scheduleIdle(() => {
            normalizeAssets(deferredHintAssets).forEach(addPrefetchHint)
            void warmAssetsInBatches(deferredAssets, deferredBatchSize)
          }, deferredDelayMs)
        })
      }, initialDelayMs)
    },
  }
}

export function imageAsset(url: string): WarmableAsset {
  return { url, as: 'image' }
}

export function videoAsset(url: string): WarmableAsset {
  return { url, as: 'video' }
}

export function audioAsset(url: string): WarmableAsset {
  return { url, as: 'audio' }
}

function isVideoBlobUrl(url: string): boolean {
  return /\.(mp4|webm)(\?.*)?$/i.test(url)
}

function isAudioBlobUrl(url: string): boolean {
  return /\.(mp3|wav|ogg|m4a)(\?.*)?$/i.test(url)
}

function setBlobWarmComplete(completeKey: string) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(completeKey, '1')
  } catch {
    /* quota / private mode / disabled */
  }
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(completeKey, '1')
    }
  } catch {
    /* sessionStorage full or disabled */
  }
}

/** True when this manifest has already completed a successful bootstrap warm (gate or atlas key). */
export function isBootstrapBlobWarmComplete(manifestFingerprint: string): boolean {
  return isBlobWarmFlagSet(blobWarmCompleteStorageKey(manifestFingerprint))
}

/** True once this manifest's warm was started at least once (legacy; prefer {@link isBootstrapBlobWarmComplete}). */
export function isBootstrapBlobWarmAttempted(manifestFingerprint: string): boolean {
  return isBlobWarmFlagSet(blobWarmAttemptedStorageKey(manifestFingerprint))
}

/** Mark this manifest as already attempted before any network work starts. */
export function markBootstrapBlobWarmAttempted(manifestFingerprint: string): void {
  setBlobWarmComplete(blobWarmAttemptedStorageKey(manifestFingerprint))
}

function clearBlobWarmFlag(storageKey: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(storageKey)
  } catch {
    /* access denied / disabled */
  }
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(storageKey)
  } catch {
    /* access denied / disabled */
  }
}

/**
 * Drop the "already warmed" flag for a manifest so the next warm actually runs.
 * Used when a cache audit finds the bytes are gone (see `auditBlobWarmCache`) — the flag
 * records that the warm loop once ran, which is NOT the same as the assets still being present.
 */
export function clearBootstrapBlobWarmComplete(manifestFingerprint: string): void {
  clearBlobWarmFlag(blobWarmCompleteStorageKey(manifestFingerprint))
}

export type BlobWarmAudit = {
  /** URLs that had to be checked (persisted "gone" URLs are excluded). */
  total: number
  /** URLs whose bytes are not present in any cache this page can read. */
  missing: number
  /** Whether `public/sw.js` is serving this page's requests at all. */
  swControlled: boolean
  /**
   * Whether `missing` is a real measurement.
   *
   * `false` means Cache Storage could not be enumerated at all (API absent, storage disabled,
   * partitioned, private mode) — so `missing` is an assumption, not evidence. Callers must NOT
   * clear persisted warm flags on an unverified audit: doing so turns one unreadable read into a
   * permanent re-download loop, because the cleared flag makes every later boot cold too.
   */
  verified: boolean
}

/**
 * Exact check of whether a manifest's assets are actually available to THIS page, as opposed to
 * the persisted warm flag, which only records that a warm loop once finished.
 *
 * Reads each cache's key list once and matches in memory — no bodies, no network — so it costs a
 * few tens of milliseconds even for a thousand URLs, cheap enough to run before every gated
 * surface is shown.
 *
 * Presence does not depend on the service worker controlling the page (see
 * `partitionUrlsBySwCache`), so this no longer blocks on SW claim before answering. It still gives
 * the worker a brief chance to take over, because a controlled page can additionally be *served*
 * from those entries.
 *
 * Reports `verified: false` rather than throwing when the caches cannot be read at all, so callers
 * can tell "verified cold" apart from "could not verify" without a try/catch.
 */
export async function auditBlobWarmCache(
  urls: readonly string[],
  manifestFingerprint: string,
): Promise<BlobWarmAudit> {
  const gone = readPersistedGoneUrls(manifestFingerprint)
  const list = Array.from(new Set(urls.filter((u) => Boolean(u) && !gone.has(u))))
  if (list.length === 0) {
    return { total: 0, missing: 0, swControlled: isPageControlledBySw(), verified: true }
  }
  if (typeof window === 'undefined') {
    return { total: list.length, missing: 0, swControlled: false, verified: false }
  }

  await waitForBlobCacheSwController()
  try {
    const { missing } = await partitionUrlsBySwCache(list)
    return {
      total: list.length,
      missing: missing.length,
      swControlled: isPageControlledBySw(),
      verified: true,
    }
  } catch {
    /* Cache Storage unreadable — report it as such instead of as a cold cache. */
    return {
      total: list.length,
      missing: list.length,
      swControlled: isPageControlledBySw(),
      verified: false,
    }
  }
}

/**
 * Ask the browser to make this origin's storage persistent (exempt from automatic eviction under
 * disk pressure). Without it, Cache Storage is "best effort" and can be cleared out from under us —
 * which is the whole failure mode this warm pipeline exists to avoid.
 *
 * Chrome grants this silently for installed/engaged sites; Safari never grants it (and evicts all
 * site data after ~7 days unused), so callers must still degrade gracefully. Never prompts.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false
  try {
    if (await navigator.storage.persisted?.()) return true
    return await navigator.storage.persist()
  } catch {
    return false
  }
}

function isLikelySameOriginAppAsset(url: string): boolean {
  if (typeof window === 'undefined') return false
  if (url.startsWith('/')) return true
  try {
    const u = new URL(url, window.location.href)
    return u.origin === window.location.origin
  } catch {
    return false
  }
}

function stripUrlSearch(url: string): string {
  const i = url.indexOf('?')
  return i < 0 ? url : url.slice(0, i)
}

/** Absolute URL string as `cache.keys()` reports it, so key lookups are plain string compares. */
function cacheKeyUrl(url: string): string {
  try {
    return new URL(url, typeof window === 'undefined' ? undefined : window.location.href).href
  } catch {
    return url
  }
}

/**
 * Split URLs into those already in `public/sw.js` caches vs not. Used only to **skip repeat work** on refresh
 * when `localStorage` was cleared but Cache Storage still holds the same build’s assets.
 *
 * Reads each cache's key list ONCE and matches in memory. The previous version issued a
 * `cache.match()` per URL, which on a cold first load is ~900 guaranteed misses — every one a
 * round trip to the cache backend, all of them before the first byte is fetched, all of them
 * answering a question ("is anything cached yet?") whose answer is no.
 *
 * `keys()` only proves an entry exists, not that it was a 2xx; `public/sw.js` stores nothing but
 * status-200 responses, so that distinction has no practical effect here.
 */
async function partitionUrlsBySwCache(
  urls: string[],
): Promise<{ cached: string[]; missing: string[] }> {
  if (urls.length === 0) return { cached: [], missing: [] }
  if (typeof caches === 'undefined' || typeof window === 'undefined') {
    return { cached: [], missing: urls }
  }
  /**
   * Cache Storage presence is read DIRECTLY, without requiring a controller.
   *
   * This used to bail out with "nothing is cached" whenever `navigator.serviceWorker.controller`
   * was null, which conflated two different questions:
   *   - "will the worker serve this request?" (needs a controller)
   *   - "are these bytes already stored?"     (does not)
   * `caches` is a window-scope API; the page can enumerate the very same caches `public/sw.js`
   * writes whether or not a worker currently controls it. Conflating them made every reload that
   * raced SW claim audit as fully cold — 981 gate URLs re-downloaded with the bytes sitting
   * right there — and, worse, `verifyVercelBlobGateWarm` then CLEARED the warm flags, so the
   * next reload started from zero as well.
   *
   * An uncontrolled page still cannot be *served* from Cache Storage, so it refetches through the
   * HTTP cache — but those assets are `immutable, max-age=31536000` (see `public/_headers`), so
   * that is a cache hit, not a download. Reporting them as present is the honest answer.
   */

  /**
   * `caches.open()` CREATES a cache that does not exist, so a name that no longer matches the one
   * `public/sw.js` writes reads back as an empty set rather than an error — every URL then audits
   * as missing and the gate re-runs forever, silently. `caches.has()` first makes that drift
   * visible instead: an absent cache is logged and reported as unreadable (`null`), which is a
   * different outcome from a present-but-empty one.
   */
  const readKeys = async (name: string): Promise<Set<string> | null> => {
    try {
      if (!(await caches.has(name))) {
        console.warn(
          `[blob-warm] Cache Storage "${name}" does not exist — nothing this page warmed can be ` +
          'verified against it. If the service worker writes a different name, the two are out of sync.',
        )
        return null
      }
      const cache = await caches.open(name)
      const keys = await cache.keys()
      return new Set(keys.map((r) => r.url))
    } catch {
      return null
    }
  }

  const [blobKeys, shellKeys] = await Promise.all([
    readKeys(ECLIPSERA_BLOB_CACHE_STORAGE_NAME),
    readKeys(ECLIPSERA_SHELL_CACHE_STORAGE_NAME),
  ])

  if (!blobKeys && !shellKeys) throw new Error('blob-warm:cache-storage-unreadable')

  /** Parity with the old shell probe, which fell back to `match(req, { ignoreSearch: true })`. */
  const shellKeysNoSearch = shellKeys && new Set([...shellKeys].map(stripUrlSearch))

  const cached: string[] = []
  const missing: string[] = []
  for (const u of urls) {
    const key = cacheKeyUrl(u)
    const hit =
      blobKeys?.has(key) === true ||
      (shellKeys !== null &&
        isLikelySameOriginAppAsset(u) &&
        (shellKeys.has(key) || shellKeysNoSearch?.has(stripUrlSearch(key)) === true))
    if (hit) cached.push(u)
    else missing.push(u)
  }
  return { cached, missing }
}

/**
 * The asset is not coming back, so retrying is pure delay.
 *
 * A URL that 404s cost 13 attempts spread over two passes, each separated by a growing backoff —
 * about 20 seconds of the loading bar per dead file, spent proving something the first response
 * already said. Only statuses that mean "this will never succeed" qualify; 5xx, 429 and network
 * failures stay retryable, because those do recover.
 */
class AssetGoneError extends Error {
  readonly status: number
  constructor(url: string, status: number) {
    super(`asset-gone:${status}:${url}`)
    this.name = 'AssetGoneError'
    this.status = status
  }
}

function isPermanentlyGoneStatus(status: number): boolean {
  return status === 404 || status === 410 || status === 403 || status === 401
}

async function decodeFetchedImageBlob(blob: Blob): Promise<void> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(blob)
      bmp.close()
      return
    } catch {
      /* GIF / older engines — fall through */
    }
  }
  const objectUrl = URL.createObjectURL(blob)
  try {
    await new Promise<void>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('image-decode'))
      img.src = objectUrl
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Warm one bootstrap/CDN URL: **full response body** is read (so videos are fully buffered in cache),
 * images are decoded once, JSON must be `ok`. Service worker `fetch` handler may satisfy from Cache Storage
 * without a network round-trip, but we still verify bytes + decode here so progress reflects real readiness.
 */
export async function warmBlobUrlWithHttpCache(
  url: string,
  opts?: { decodeImages?: boolean },
): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const decodeImages = opts?.decodeImages !== false

  const fetchBlob = async (): Promise<Blob | null> => {
    try {
      const res = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'default',
      })
      if (!res.ok) {
        if (isPermanentlyGoneStatus(res.status)) throw new AssetGoneError(url, res.status)
        return null
      }
      const blob = await res.blob()
      return blob.size > 0 ? blob : null
    } catch (e) {
      if (e instanceof AssetGoneError) throw e
      return null
    }
  }

  if (/\.json(\?|$)/i.test(url)) {
    const blob = await fetchBlob()
    return blob !== null
  }

  if (isVideoBlobUrl(url)) {
    const blob = await fetchBlob()
    return blob !== null
  }

  if (isAudioBlobUrl(url)) {
    /**
     * Fetched, not played through an `<audio>` element.
     *
     * The element approach reported success unconditionally — `onerror` ran the same `done()` as
     * `oncanplaythrough` and the function then returned a hard `true`, so a 404 or a network drop
     * counted as warmed. Worse, `<audio>` uses ranged requests, and `public/sw.js` deliberately
     * does not store 206 responses, so even a successful preload often cached nothing while the
     * warm recorded it as complete. A plain GET stores the full 200 body the way every other
     * asset does, and its result is actually checked.
     */
    const blob = await fetchBlob()
    return blob !== null
  }

  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'default',
    })
    if (!res.ok) {
      if (isPermanentlyGoneStatus(res.status)) throw new AssetGoneError(url, res.status)
      return false
    }
    const blob = await res.blob()
    if (blob.size === 0) return false
    /**
     * Decoding here proves the bytes are a valid image, but the bitmap is closed immediately and
     * the browser decodes again when the image is actually painted — so it is duplicated work that
     * allocates (a 4 MP asset is ~17 MB of RGBA) and churns GC.
     *
     * Worth it for the gate, where the point is that the first screen is genuinely ready. Pure
     * waste for background phases, which are speculative and paint nothing: callers pass
     * `decodeImages: false` there, turning ~1500 discarded decodes into zero.
     */
    if (decodeImages) await decodeFetchedImageBlob(blob)
    return true
  } catch (e) {
    if (e instanceof AssetGoneError) throw e
    return await new Promise<boolean>((resolve) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      let settled = false
      const finish = (ok: boolean) => {
        if (settled) return
        settled = true
        resolve(ok)
      }
      img.onload = () => finish(true)
      img.onerror = () => finish(false)
      img.src = url
    })
  }
}

/**
 * Set by `src/index.tsx` to the in-flight `navigator.serviceWorker.register()` promise.
 *
 * Registration is fired eagerly at module scope and not awaited, so on a reload the warm could
 * start, find no registration yet, and give up before the worker ever appeared. Polling
 * `getRegistration()` for a fixed 2s was a guess at that race; awaiting the actual promise settles
 * it. Stays `null` when the app never registers a worker (maintenance mode, unsupported browser),
 * in which case the poll below is still the best available answer.
 */
let swRegistrationPromise: Promise<ServiceWorkerRegistration | undefined> | null = null

/** Called once from the entry point with the registration promise; never throws to the caller. */
export function setBlobCacheSwRegistrationPromise(
  p: Promise<ServiceWorkerRegistration | undefined>,
): void {
  swRegistrationPromise = p.catch(() => undefined)
}

/**
 * Grace period for a worker that is active but has not claimed this page yet.
 *
 * Short on purpose: a hard reload or DevTools bypass will never produce a `controllerchange`, and
 * the audit no longer depends on being controlled, so this only buys the common reload case where
 * `clients.claim()` resolves a few ms after `ready`.
 */
const SW_CLAIM_GRACE_MS = 300

/** Wait until the app is controlled by the registered SW so blob `fetch` hits the same Cache Storage as install precache. */
async function waitForBlobCacheSwController(maxWaitMs = 5000): Promise<void> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }
  try {
    // Await the registration the entry point actually started, rather than racing it.
    if (swRegistrationPromise) {
      try {
        await swRegistrationPromise
      } catch {
        /* registration failed — fall through to the poll below */
      }
    }
    let reg = await navigator.serviceWorker.getRegistration()
    if (!reg) {
      const deadline = Date.now() + 2000
      while (!reg && Date.now() < deadline) {
        await new Promise<void>((r) => {
          window.setTimeout(r, 50)
        })
        reg = await navigator.serviceWorker.getRegistration()
      }
    }
    if (!reg) return
    await navigator.serviceWorker.ready
    if (navigator.serviceWorker.controller) return
    /**
     * A worker only claims pages while it activates. If the registration is already settled —
     * active worker activated, nothing installing or waiting — and this page still has no
     * controller, it was loaded past the worker on purpose (hard reload, DevTools bypass) and no
     * `controllerchange` is coming. Waiting the full timeout here just added 5 s of dead time to
     * every loading screen on such a page.
     *
     * `claim()` in the worker's `activate` can still land a beat after `ready` resolves, so give
     * it a short grace period rather than none. This is only about being *served* from Cache
     * Storage — presence is read directly (see `partitionUrlsBySwCache`), so an uncontrolled page
     * no longer mis-audits as cold and nothing here can trigger a re-download.
     */
    const settledWithoutClaim = reg.active?.state === 'activated' && !reg.installing && !reg.waiting
    const waitMs = settledWithoutClaim ? SW_CLAIM_GRACE_MS : maxWaitMs
    await new Promise<void>((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        window.clearTimeout(t)
        navigator.serviceWorker.removeEventListener('controllerchange', finish)
        resolve()
      }
      const t = window.setTimeout(finish, waitMs)
      navigator.serviceWorker.addEventListener('controllerchange', finish)
    })
  } catch {
    /* ignore */
  }
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

/**
 * Per-asset progress, for showing what the loading bar is actually working on and for finding the
 * assets that dominate a cold start. `ms` is wall-clock for the whole warm of that URL including
 * any retries, which is the number worth optimising.
 */
export type AssetWarmEvent =
  | { kind: 'start'; url: string; inFlight: number }
  | { kind: 'done'; url: string; ms: number; outcome: 'ok' | 'failed' | 'gone'; inFlight: number }

export type PreloadBlobUrlsOptions = {
  /**
   * Called as each URL starts and finishes. Fires once per asset per phase, so a subscriber that
   * touches React state must throttle — see `vercelAssetPreloadGate`.
   */
  onAssetEvent?: (event: AssetWarmEvent) => void
  /**
   * When true, failed URLs after retries do not throw: warm-complete is still written so refresh does not
   * re-download the whole manifest. Used for background atlas / overlay pipelines only.
   */
  allowPartialComplete?: boolean
  /**
   * Verify each image by decoding it once (default true). Set false for speculative background
   * phases: the decode is thrown away immediately and the browser decodes again at paint time, so
   * for assets nothing is about to render it is pure allocation and GC churn.
   */
  decodeImages?: boolean
}

/**
 * Preloads many blob URLs with bounded concurrency and progress (startup bootstrap).
 * Each URL advances the bar only after a **successful** warm (`warmBlobUrlWithHttpCache`).
 * Repeat visits: `localStorage` (`eclipsera.blobWarmComplete.v6.${fingerprint}`) skips all work. If that flag is
 * missing but every URL is already in the service worker caches (e.g. storage cleared), we mark complete without
 * re-reading every body. Otherwise each URL is warmed until success.
 *
 * Waits for the blob service worker to control the page when possible so `fetch` is consistently routed through `public/sw.js`.
 */
export async function preloadBlobUrlsWithProgress(
  urls: readonly string[],
  onProgress: (loaded: number, total: number) => void,
  concurrency = 6,
  manifestFingerprint = 'default',
  options?: PreloadBlobUrlsOptions,
): Promise<void> {
  const list = Array.from(new Set(urls.filter(Boolean)))
  const total = list.length
  if (total === 0) {
    onProgress(0, 0)
    return
  }

  const completeKey = blobWarmCompleteStorageKey(manifestFingerprint)
  if (isBlobWarmFlagSet(completeKey)) {
    onProgress(total, total)
    return
  }

  /**
   * Publish the denominator before doing anything slow. What follows — waiting for the service
   * worker to take control (up to 5s) and scanning Cache Storage — reports nothing, so the
   * loading screen used to sit on `total === 0`, i.e. an empty bar and an indeterminate "Loading…"
   * label. On a start where every asset turns out to be cached, the next event was the finish, and
   * the player watched a bar that never moved off zero disappear.
   */
  onProgress(0, total)

  await waitForBlobCacheSwController()

  let cached: string[] = []
  let missing: string[] = list
  try {
    ; ({ cached, missing } = await partitionUrlsBySwCache(list))
  } catch {
    /* caches unreadable — nothing can be skipped, so warm everything */
  }

  let completed = cached.length
  onProgress(completed, total)

  if (missing.length === 0) {
    setBlobWarmComplete(completeKey)
    return
  }

  const decodeImages = options?.decodeImages !== false
  /** 404/410/403 — no retry, no second pass, and not counted as a failure worth throwing over. */
  const gone = new Set<string>()
  const tryWarmUrl = async (url: string, attempts: number): Promise<boolean> => {
    for (let a = 0; a < attempts; a++) {
      try {
        if (await warmBlobUrlWithHttpCache(url, { decodeImages })) return true
      } catch (e) {
        if (e instanceof AssetGoneError) {
          gone.add(url)
          return false
        }
        throw e
      }
      if (a + 1 < attempts) {
        await new Promise<void>((r) => {
          window.setTimeout(r, 200 * (a + 1))
        })
      }
    }
    return false
  }

  const onAsset = options?.onAssetEvent
  let inFlight = 0
  const emit = (e: AssetWarmEvent) => {
    if (!onAsset) return
    try {
      onAsset(e)
    } catch {
      /* a reporting hook must never break the warm */
    }
  }

  const processList = async (
    urlsToProcess: string[],
    attemptsPerUrl: number,
    workerLimit: number,
  ): Promise<string[]> => {
    const warmTotal = urlsToProcess.length
    if (warmTotal === 0) return []
    const limit = Math.max(1, Math.min(workerLimit, warmTotal))
    const failedOut: string[] = []
    let cursor = 0

    const worker = async () => {
      while (true) {
        const i = cursor
        cursor += 1
        if (i >= warmTotal) return
        const url = urlsToProcess[i]
        const startedAt = nowMs()
        inFlight += 1
        emit({ kind: 'start', url, inFlight })
        const ok = await tryWarmUrl(url, attemptsPerUrl)
        inFlight -= 1
        emit({
          kind: 'done',
          url,
          ms: nowMs() - startedAt,
          outcome: ok ? 'ok' : gone.has(url) ? 'gone' : 'failed',
          inFlight,
        })
        if (ok) {
          completed += 1
          onProgress(completed, total)
        } else {
          failedOut.push(url)
        }
      }
    }

    await Promise.all(Array.from({ length: limit }, () => worker()))
    return failedOut
  }

  let failed = await processList(missing, 5, concurrency)

  // Dead URLs cannot be rescued by a second pass; only genuinely-failed ones get one.
  failed = failed.filter((u) => !gone.has(u))

  if (failed.length > 0) {
    failed = await processList(failed, 8, Math.max(2, Math.min(4, concurrency)))
    failed = failed.filter((u) => !gone.has(u))
  }

  if (gone.size > 0) {
    console.warn('[blob-warm] skipped assets the CDN reports as gone', {
      count: gone.size,
      sample: [...gone].slice(0, 12),
    })
    // Remembered so the pre-show audit does not count them as "missing" on every later boot.
    persistGoneUrls(manifestFingerprint, gone)
    // They will never load, so they must not hold the bar below 100% or trip the count check.
    completed += gone.size
    onProgress(Math.min(completed, total), total)
  }

  if (failed.length > 0) {
    if (options?.allowPartialComplete) {
      console.warn('[blob-warm] partial complete; persisting warm key for future sessions', {
        failed: failed.length,
        total,
        fingerprint: manifestFingerprint,
        sample: failed.slice(0, 12),
      })
      /**
       * Record them the same way genuinely-dead URLs are recorded.
       *
       * Marking the manifest complete while some URLs are NOT in the cache set up a loop that the
       * player saw as a loading screen on every single visit to the area: the next audit found
       * exactly these URLs missing, cleared the warm flag, and re-ran the entire manifest — 1,278
       * files for MvM — only to fail on the same handful and persist the same optimistic flag
       * again. Excluding them from the audit makes "partial" actually mean partial: everything
       * that CAN be cached is treated as done, and these load on demand.
       */
      persistGoneUrls(manifestFingerprint, new Set(failed))
      onProgress(total, total)
      setBlobWarmComplete(completeKey)
      return
    }
    console.error('[blob-warm] incomplete after retries', {
      failed: failed.length,
      total,
      sample: failed.slice(0, 16),
    })
    throw new Error(`blob-warm:incomplete:${failed.length}`)
  }

  if (completed !== total) {
    if (options?.allowPartialComplete) {
      console.warn('[blob-warm] count mismatch; persisting warm key', { completed, total, manifestFingerprint })
      onProgress(total, total)
      setBlobWarmComplete(completeKey)
      return
    }
    console.error('[blob-warm] count mismatch', { completed, total })
    throw new Error('blob-warm:count-mismatch')
  }

  setBlobWarmComplete(completeKey)
}
